<template>
  <v-container mt-7>
    <v-form ref="form" lazy-validation>
      <v-row>
        <v-col class="selectorColumn">
          <v-select
            v-model="orgChoice"
            :items="organizations"
            label="Organizations"
            :disabled="orgDisabled"
            outlined
            dense
            clearable
            @click:clear="$nextTick(() => (orgChoice = null))"
          >
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="insChoice"
            :items="insurances"
            label="Select Insurance"
            multiple
            clearable
            outlined
            dense
            required
            :rules="[(v) => (v && v.length !== 0) || 'Insurance is required']"
            @click:clear="$nextTick(() => (insChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn" v-if="tab === 1">
          <template v-slot:selection="{ item, index }">
            <span>{{ item.Provide_Name }}</span>
            <span v-if="index === 1" class="grey--text caption">+{{ provChoice.length - 1 }}</span>
          </template>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDate"
                label="Start Date"
                show-current="2018-01"
                append-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              type="month"
              @input="menu = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDate"
                label="End Date"
                append-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              type="month"
              @input="menu2 = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-btn @click="validate" color="secondary" depressed block class="py-5">
            Run Report
            <v-icon>mdi-play</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-row>
      <v-col v-if="load" cols="12" class="text-center">
        <v-progress-circular
          color="secondary"
          indeterminate
          :size="50"
          :width="3"
        ></v-progress-circular>
      </v-col>
      <v-col v-else-if="insChoice.length === 0 && !showReport" cols="12">
        <v-alert :value="true" type="info"> Please select all required fields </v-alert>
      </v-col>
      <v-col v-else-if="insChoice.length > 0 && !showReport" cols="12">
        <v-alert :value="true" type="info"> Press run report to continue </v-alert>
      </v-col>
      <v-col v-else-if="showReport && changeDetected && insChoice.length > 0" cols="12">
        <v-alert :value="true" type="warning">
          Change detected: Run the report to see updated data
        </v-alert>
      </v-col>
      <v-col v-else-if="reportData.length === 0 && showReport" cols="12">
        <v-alert :value="true" type="info">
          There is no data available for this selection. Please change your filter selections and
          run report again
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card v-for="item in items" :key="item">
          <ins-comp
            v-if="item === 'Insurance'"
            :organization="orgChoice"
            :providers="provChoice"
            :insurance="insChoice"
            :startDate="startDate"
            :endDate="endDate"
            :reportData="reportData"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import axios from 'axios';
import insComp from '../components/contractComparison/insComp';
import provComp from '../components/contractComparison/provComp';

export default {
  components: {
    insComp,
    provComp,
  },
  data() {
    return {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 3)
        .toISOString()
        .substr(0, 7),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7),
      menu: false,
      modal: false,
      menu2: false,
      tab: null,
      insChoice: [],
      orgChoice: null,
      provChoice: [],
      user: '',
      organizations: [],
      providers: [],
      insurances: [],
      locations: [],
      items: ['Insurance'],
      currentItem: '',
      reportData: [],
      load: false,
      reportChoice: [],
      changeDetected: false,
      showReport: false,
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    validate() {
      if (this.$refs.form.validate()) {
        this.getPatients();
      }
    },
    async getPatients() {
      this.load = true;

      // clear report data
      this.data = [];

      const insPart = Array.isArray(this.insChoice)
        ? this.insChoice.map(String).join(',')
        : String(this.insChoice || '');
      const url = `/api/contract/insurance=${insPart}/start=${this.startDate}/end=${this.endDate}`;

      // Check for pending request and if so cancel old one for new incoming
      this.cancelSearch();
      // Set the CancelToken
      this.cancelSource = axios.CancelToken.source();

      axios.get(url, { cancelToken: this.cancelSource.token }).then((response) => {
        // Set cancelsource back to null
        this.cancelSource = null;
        this.reportData = response.data;
        this.showReport = true;
        this.changeDetected = false;
        this.load = false;
      });
    },
    async checkPresets() {
      const r = this.user && this.user.role ? String(this.user.role).toLowerCase() : '';
      if (r === 'provider' || r === 'group') {
        await this.setPresets();
      } else if (r === 'clinical') {
        this.$router.push('/membership');
      } else {
        this.getOrgs();
        this.getInsurances();
      }
    },
    async setPresets() {
      const r = this.user && this.user.role ? String(this.user.role).toLowerCase() : '';
      if (r === 'provider' && this.tab === 1) {
        const ins = this.user.insurance;
        this.insurances = Array.isArray(ins) ? ins.slice() : ins != null && ins !== '' ? [ins] : [];
        this.insChoice = Array.isArray(ins) ? ins.slice() : ins != null && ins !== '' ? [ins] : [];
        this.orgChoice = this.user.organization;
        this.organizations = Array.isArray(this.user.organization)
          ? this.user.organization
          : this.user.organization != null && this.user.organization !== ''
            ? [this.user.organization]
            : [];
      } else {
        this.getOrgs();
        this.getInsurances();
      }
    },
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
        })
        .catch((err) => this.$router.push('/login'));
      this.checkPresets();
    },

    async getOrgs() {
      // await axios.get('api/list/organization').then((response) => {
      //   this.organizations = response.data;
      //   this.orgChoice = this.organizations;
      // });
      this.organizations = ['Clearspring', 'Humana'];
    },
    async getInsurances() {
      this.syncInsurancesFromOrg();
    },
    /** Lista de seguros disponíveis conforme a organização (Humana → só Humana no portal). */
    syncInsurancesFromOrg() {
      const all = ['Clearspring', 'Humana', 'Careplus'];
      const o =
        this.orgChoice != null && this.orgChoice !== ''
          ? String(this.orgChoice).toLowerCase()
          : '';
      if (o === 'humana') {
        this.insurances = ['Humana'];
      } else {
        this.insurances = all.slice();
      }
    },
    async setStartDate(date) {
      this.startDate = date;
    },
    async setEndDate(date) {
      this.endDate = date;
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
  },
  watch: {
    orgChoice() {
      this.syncInsurancesFromOrg();
      const allowed = new Set(this.insurances);
      this.insChoice = (this.insChoice || []).filter((x) => allowed.has(String(x)));
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    async startDate() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    async endDate() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    async insChoice() {
      this.reportData = [];
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
  },
  computed: {
    orgDisabled() {
      if (
        (this.user && String(this.user.role).toLowerCase() === 'provider') ||
        (this.user && String(this.user.role).toLowerCase() === 'group')
      ) {
        return true;
      }
      return false;
    },
    insDisabled() {
      if (
        (this.user && String(this.user.role).toLowerCase() === 'provider') ||
        (this.user && String(this.user.role).toLowerCase() === 'group')
      ) {
        return true;
      }
      return false;
    },
  },
};
</script>
